/**
 * Where the API lives.
 *
 * Read from REACT_APP_BASE_URL at BUILD time — Create React App inlines
 * process.env.REACT_APP_* into the bundle during `npm run build`, so this is
 * fixed per deployment and cannot be changed afterwards without rebuilding.
 *
 * ── Why the hardcoded localhost was removed ─────────────────────────────────
 * This file previously read:
 *
 *     export const baseUrl = "http://localhost:5003";
 *
 * with the environment-variable logic commented out beneath it. That works on
 * a developer machine and is silently broken everywhere else: in a deployed
 * build, `localhost` resolves to the VISITOR's own computer, not the server.
 * Every API call from the hosted site would fail with a connection error that
 * looks like the backend is down.
 *
 * The CI lint failure ("configuredBaseUrl is assigned a value but never used")
 * was the only thing preventing that build from shipping.
 *
 * ── Local development ───────────────────────────────────────────────────────
 * Set REACT_APP_BASE_URL in ReactJs/.env.local — do NOT edit this file:
 *
 *     REACT_APP_BASE_URL=http://localhost:5003
 *
 * .env.local is git-ignored by Create React App convention, so a local
 * override cannot be committed by accident. That is precisely the mistake this
 * comment exists to prevent recurring.
 */

import { runtimeEnv } from "./runtimeEnv";

/** Trailing slashes break path joins: `${baseUrl}/auth/login` → `//auth/login`. */
const stripTrailingSlash = (value) => value.replace(/\/+$/, "");

/**
 * Runtime first: in Docker, REACT_APP_BASE_URL on the frontend CONTAINER
 * (written to /env-config.js at start) overrides the value baked in at build
 * time — no rebuild needed to point the UI at another backend.
 */
const configured = runtimeEnv("REACT_APP_BASE_URL", process.env.REACT_APP_BASE_URL);
const configuredWs = runtimeEnv("REACT_APP_WS_URL", process.env.REACT_APP_WS_URL);

/**
 * Empty string means SAME ORIGIN, and that is a supported, preferred setting —
 * not a missing value.
 *
 * The all-in-one container serves the React app and proxies the API from one
 * nginx, so the bundle should issue relative requests: `/api/...` rather than
 * `https://some-host/api/...`. Setting REACT_APP_BASE_URL=/ produces exactly
 * that, and the resulting build works unchanged on localhost, a LAN address
 * and a public domain — no rebuild per environment, and no CORS at all.
 *
 * The absolute fallback below is only for builds hosted somewhere else
 * (Cloudflare Pages), where the API genuinely lives on another origin.
 */
export const baseUrl =
  configured === undefined || configured === null
    ? "https://app-limo.kenshinsystems.com"
    : stripTrailingSlash(configured);

/**
 * WebSocket origin for live tracking and notifications.
 *
 * When baseUrl is same-origin (empty), it CANNOT be derived by string
 * substitution — `"".replace(/^http/, "ws")` is still an empty string, and a
 * socket opened against that silently never connects. In that case the origin
 * has to come from the page itself, which also gets the scheme right: wss on
 * an HTTPS page, ws on plain HTTP.
 */
const sameOriginWs = () => {
  if (typeof window === "undefined") return "";
  return window.location.origin.replace(/^http/, "ws");
};

export const wsUrl = configuredWs
  ? stripTrailingSlash(configuredWs)
  : baseUrl
  ? baseUrl.replace(/^http/, "ws")
  : sameOriginWs();

export default baseUrl;
